import { Dayjs } from "dayjs";
import HttpClient from "../../../../core/HttpClient";
import { Authentication } from "@leedpulse-monorepo/classes";
import { ISubscription } from "@leedpulse-monorepo/interfaces";

export interface CallsAggregate {
    handledCalls: number;
    answeredCalls: number;
    missedCalls: number;
    answerRate: number;
    totalDurationSeconds: number;
    averageDurationMinutes: number;
    /** La même moyenne en secondes entières : la tuile affiche « 2 min 22 s ». */
    averageDurationSeconds: number;
    totalCost: number;
    coverage: { pricedCalls: number; answeredCalls: number };
    faulty: { total: number; byReason: Record<string, number> };
    breakdown: {
        direction: Record<string, number>;
        platform: Record<string, number>;
    };
}

export interface OverviewBucket {
    timestamp: number;
    // Tout appel non-défaut, quelle que soit sa direction — même identité que
    // `handledCalls` de `CallsAggregate`. Ne pas la redériver de
    // `inbound + outbound` : un appel à direction inconnue compte ici mais
    // dans aucun des deux (backend/src/helpers/leedflow/overview-model.ts).
    handled: number;
    answered: number;
    missed: number;
    inbound: number;
    outbound: number;
    durationSeconds: number;
    // Appels en défaut, hors de `handled` et de toutes les autres mesures.
    // Affichés sur le graphe pour rester visibles jusqu'à réparation.
    faulty: number;
}

export interface SmsStats {
    sentCount: number;
    receivedCount: number;
    totalCost: number;
    averageCost: number;
    /** Coût des SMS émis, séparé du reçu : l'envoi coûte bien plus cher à l'unité. */
    sentCost: number;
    receivedCost: number;
}

export interface VoicemailStats {
    totalCount: number;
    totalCost: number;
}

export interface CostStats {
    openaiCount: number;
    openaiCost: number;
    elevenlabsCount: number;
    elevenlabsCost: number;
}

export interface TopSubscriptionDevice {
    platform: string | null;
    model: string | null;
    manufacturer: string | null;
    osVersion: string | null;
    appVersion: string | null;
    lastSeen: number | null;
}

/** Qui tient la ligne, tel que le backend l'a résolu. */
export interface TopSubscriptionHolder {
    name: string;
    /** L'avatar arrive à part, après les chiffres (cf. `getOverviewAvatars`). */
    uid: string;
}

export interface TopSubscriptionEntry {
    id: string;
    name: string;
    /** Appels traités par l'agent, réceptionniste IA exclue. */
    calls: number;
    /** Appels pris par la réceptionniste IA, comptés à part de `calls`. */
    receptionist: number;
    sms: number;
    cost: number;
    /** Appels non-défaut par plateforme — de quoi dire GSM, VoIP, ou les deux. */
    platforms: Record<string, number>;
    /** Dernier appel ou SMS de la période. */
    lastActivity: number | null;
    /** Appareil du dernier utilisateur mobile vu sur cet abonnement. */
    device: TopSubscriptionDevice | null;
    /** Le même utilisateur que `device`. `null` s'il n'y en a aucun. */
    holder: TopSubscriptionHolder | null;
    /** Premier numéro émetteur de l'abonnement. */
    sender: string | null;
}

/**
 * Quatre classements indépendants, pas un seul retrié côté front : miroir de
 * `TopSubscriptions` (backend/src/helpers/leedflow/overview-model.ts). Un
 * abonnement fort en SMS mais absent du top appels doit pouvoir apparaître
 * dans `bySms` sans dépendre de `byCalls`.
 */
export interface TopSubscriptions {
    byCalls: TopSubscriptionEntry[];
    byReceptionist: TopSubscriptionEntry[];
    bySms: TopSubscriptionEntry[];
    byCost: TopSubscriptionEntry[];
}

export interface LastCallEntry {
    id: string;
    created: number;
    direction: string | null;
    customer: string | null;
    sender: string | null;
    /** Nom de l'abonnement, `null` s'il n'a pas pu être résolu : on affiche alors `sender`. */
    subscriptionName: string | null;
    /** Prénom nom du titulaire de l'abonnement, `null` sans utilisateur mobile. */
    holderName: string | null;
    duration: number | null;
    missed: boolean | null;
    platform: string | null;
    transcription: string | null;
    /**
     * Appareil de l'agent, résolu côté backend via `voip.id_owner_uid`. `null`
     * sur un appel GSM : il ne passe pas par l'app, aucun appareil n'y est
     * attaché.
     */
    device: string | null;
    /**
     * Le contact est marqué confidentiel par son propriétaire. La ligne reste
     * affichée, et l'enregistrement reste écoutable depuis le backoffice ;
     * seule la synthèse IA tombe — `transcription` arrive déjà vide du backend,
     * le contenu n'est pas seulement caché, il n'est pas envoyé.
     */
    confidential: boolean;
}

/**
 * Une ligne de « Derniers SMS ». Miroir de `LastSmsEntry`
 * (backend/src/helpers/leedflow/overview-model.ts) : `preview` est déjà tronqué
 * côté serveur, `length` compte le message entier.
 */
export interface LastSmsEntry {
    id: string;
    created: number;
    /** `assistant` = envoyé par Leedflow, `user` = reçu. */
    role: string | null;
    customer: string | null;
    sender: string | null;
    /** Cf. `LastCallEntry.subscriptionName`. */
    subscriptionName: string | null;
    /** Cf. `LastCallEntry.holderName`. */
    holderName: string | null;
    platform: string | null;
    preview: string | null;
    length: number | null;
    segments: number | null;
    /** Même règle que `LastCallEntry.confidential` : ici c'est `preview` qui arrive vide. */
    confidential: boolean;
}

/**
 * Une ligne d'appel en défaut. Ni durée ni état : ces documents n'en ont pas
 * d'exploitable (miroir de `FaultyCallEntry`,
 * backend/src/helpers/leedflow/overview-model.ts).
 */
export interface FaultyCallEntry {
    id: string;
    created: number;
    direction: string | null;
    customer: string | null;
    sender: string | null;
    platform: string | null;
    reason: string;
}

/** A subscription present on a map point. */
export interface MapSubscriptionEntry {
  subscriptionId: string;
  subscriptionName: string;
  sender: string | null; // map search matches it as well as the name
  ips: string[]; // what placed these users, to check a suspect row
  users: number;
}

/** A place on the map, and who is there. */
export interface MapPoint {
  id: string;
  label: string;
  lon: number;
  lat: number;
  users: number;
  subscriptions: MapSubscriptionEntry[];
}

export interface OverviewStats {
    callsStats: CallsAggregate;
    smsStats: SmsStats;
    voicemailStats: VoicemailStats;
    costStats: CostStats;
    topSubscriptions: TopSubscriptions;
    lastCalls: LastCallEntry[];
    lastSms: LastSmsEntry[];
    // Échantillon plafonné côté backend (`FAULTY_CALLS_LIMIT`) : le compteur
    // `callsStats.faulty.total` reste, lui, exhaustif.
    faultyCalls: FaultyCallEntry[];
    // Répartition des utilisateurs mobiles par plateforme (`ios` / `android`).
    // Ne dépend PAS de la période, contrairement aux deux autres dimensions de
    // la Répartition (cf. `buildDeviceBreakdown` côté backend).
    deviceBreakdown: Record<string, number>;
    /**
     * Map points, built from the addresses actually recorded on the users and
     * placed against the local DB-IP dataset. Empty when none can be placed, and
     * the map says so rather than inventing points.
     */
    mapPoints: MapPoint[];
    buckets: OverviewBucket[];
    /**
     * Agrégat de la fenêtre de comparaison, absent sous
     * `MIN_CALLS_FOR_DELTA` appels traités. `period` est la fenêtre que le
     * backend a réellement interrogée (`previousPeriod`) : jusqu'à 7 jours de
     * sélection il recule d'une semaine pile — un samedi se compare au samedi
     * précédent — et de la durée de la période au-delà. Elle sert à écrire
     * contre quoi l'écart se lit, sans redériver la règle côté front.
     */
    previous?: { callsStats: CallsAggregate; period: { from: number; to: number } };
    /**
     * Blocs que le backend n'a pas pu calculer, avec le motif brut. Affiché
     * derrière « Voir plus » dans le bandeau : sans la cause, une dégradation
     * partielle se contemple sans jamais se corriger.
     */
    partial?: { block: string; message: string }[];
}

const EMPTY_CALLS_AGGREGATE: CallsAggregate = {
    handledCalls: 0,
    answeredCalls: 0,
    missedCalls: 0,
    answerRate: 0,
    totalDurationSeconds: 0,
    averageDurationMinutes: 0,
    averageDurationSeconds: 0,
    totalCost: 0,
    coverage: { pricedCalls: 0, answeredCalls: 0 },
    faulty: { total: 0, byReason: {} },
    breakdown: { direction: {}, platform: {} },
};

const EMPTY: OverviewStats & { subscriptions: ISubscription[]; subscriptionApiKeys: string[] } = {
    callsStats: EMPTY_CALLS_AGGREGATE,
    smsStats: { sentCount: 0, receivedCount: 0, totalCost: 0, averageCost: 0, sentCost: 0, receivedCost: 0 },
    voicemailStats: { totalCount: 0, totalCost: 0 },
    costStats: { openaiCount: 0, openaiCost: 0, elevenlabsCount: 0, elevenlabsCost: 0 },
    topSubscriptions: { byCalls: [], byReceptionist: [], bySms: [], byCost: [] },
    lastCalls: [],
    lastSms: [],
    faultyCalls: [],
    deviceBreakdown: {},
    mapPoints: [],
    buckets: [],
    subscriptions: [],
    subscriptionApiKeys: [],
};

/**
 * Fetches the Leedflow overview stats from the backend (uses the Admin SDK
 * server-side, which bypasses Firestore security rules and the missing
 * client-side rules on the Costs collection).
 */
async function getOverviewStats(
    auth: Authentication | null,
    period?: [Dayjs | null, Dayjs | null],
    subscriptionIds?: string[],
    exclude?: string[],
): Promise<OverviewStats & { subscriptions: ISubscription[]; subscriptionApiKeys: string[] }> {
    if (!auth) return EMPTY;

    const body: {
        period?: { from: number; to: number };
        subscriptionIds?: string[];
        exclude?: string[];
        previous: boolean;
    } = {
        previous: true,
    };

    if (period && period[0] && period[1]) {
        body.period = {
            from: period[0].startOf('day').toDate().getTime(),
            to: period[1].endOf('day').toDate().getTime(),
        };
    }
    if (subscriptionIds && subscriptionIds.length > 0) {
        body.subscriptionIds = subscriptionIds;
    }
    if (exclude && exclude.length > 0) {
        body.exclude = exclude;
    }

    const response = await HttpClient.post('/leedflow/overview-stats', body, auth.token);

    // Le backoffice (Firebase Hosting) et le backend (Kubernetes) ne se
    // déploient pas ensemble. Un front en avance reçoit l'ancienne charge
    // utile, qui n'a ni `buckets` ni `topSubscriptions` : sans ce garde-fou,
    // toute la page part en écran blanc. On bascule sur le chemin d'erreur
    // que le hook et `GeneralTab` savent déjà afficher.
    if (!response || !Array.isArray((response as OverviewStats).buckets)) {
        throw new Error('Réponse /leedflow/overview-stats inattendue : backend non à jour ?');
    }

    const stats = response as OverviewStats & { subscriptions: ISubscription[]; subscriptionApiKeys: string[] };

    // Same deploy skew as above, but neither the map nor the AI receptionist
    // tab justifies bringing the page down: an older backend leaves them empty
    // and the rest stays readable.
    return {
        ...stats,
        mapPoints: stats.mapPoints ?? [],
        topSubscriptions: { ...stats.topSubscriptions, byReceptionist: stats.topSubscriptions.byReceptionist ?? [] },
    };
}

export default getOverviewStats;
